import React from 'react';
import {
  Dialog,
  Button,
  FormControl,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { IInspection } from 'redux/conductInspectionSlice';

import './StartInspectionModal.scss';
import { useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import FormInput from 'components/FormInput';
import moment from 'moment';
import { FormLabel } from 'react-bootstrap';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { userRoles } from 'constants/constants';

type StartInspectionModalProps = {
  open: boolean;
  inspection: IInspection;
  onClose: () => void;
};

export const StartInspectionModal: React.FC<StartInspectionModalProps> = ({
  open,
  inspection,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const project = useSelector((state: RootStates) =>
    state.project.projects.find((item) => item.projectInfo.id === projectId),
  );

  const subProjects = Object.values({
    subProject: inspection.subProject?.name ?? '',
    subTask: inspection.subTask?.name ?? '',
    endTask: inspection.endTask?.name ?? '',
  })
    .filter((value) => value)
    .join(' -> ');

  const handleSubmit = () => {
    if (
      inspection.status === WorkFlowStatus.DRAFT ||
      inspection.status === WorkFlowStatus.SCHEDULED
    ) {
      navigate(`/project/${projectId}/conduct-inspection/${inspection._id}/1`);
    } else {
      navigate(`/project/${projectId}/inspection/${inspection._id}/review`);
    }

    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getInspectionTitle = () => {
    if (inspection.status === WorkFlowStatus.DRAFT) {
      return t('modal_title_complete_inspection');
    } else if (inspection.status === WorkFlowStatus.SCHEDULED) {
      return project?.role === userRoles.DOER
        ? t('title_inspection')
        : t('modal_title_start_inspection');
    }

    return t('modal_title_review_inspection');
  };

  const getStartButtonLabel = () => {
    if (inspection.status === WorkFlowStatus.SCHEDULED) {
      return t('btn_start');
    } else if (inspection.status === WorkFlowStatus.DRAFT) {
      return t('btn_continue');
    }

    return t('btn_review');
  };

  const getDateLabel = () => {
    if (inspection.isCancelled) {
      return t('label_cancel_date');
    } else if (inspection.status === WorkFlowStatus.SCHEDULED) {
      return t('label_scheduled_date');
    } else if (inspection.status === WorkFlowStatus.DRAFT) {
      return t('label_draft_date');
    }

    return inspection.status === WorkFlowStatus.OPEN
      ? t('label_inspection_start_date')
      : t('label_inspection_close_date');
  };

  const getDate = () => {
    if (inspection.isCancelled) {
      return inspection.cancelDate ?? inspection.updated_at;
    } else if (inspection.status === WorkFlowStatus.SCHEDULED) {
      return inspection.scheduledDate;
    }

    return inspection.updated_at;
  };

  const renderDate = () => {
    if (
      inspection.isCancelled ||
      inspection.status === WorkFlowStatus.DRAFT ||
      inspection.status === WorkFlowStatus.SCHEDULED
    ) {
      return (
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('label_date')}>{`${getDateLabel()}: `}</FormLabel>
          <b className="px-3">{moment(getDate()).format('DD/MM/YYYY')}</b>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column">
        {inspection.scheduledDate && (
          <div className="d-flex flex-nowrap justify-content-between">
            <FormLabel name={t('label_scheduled_date')}>{`${t(
              'label_scheduled_date',
            )}: `}</FormLabel>
            <b className="px-3">
              {moment(inspection.scheduledDate).format('DD/MM/YYYY')}
            </b>
          </div>
        )}
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('label_inspection_start_date')}>{`${t(
            'label_inspection_start_date',
          )}: `}</FormLabel>
          <b className="px-3">
            {moment(inspection.inspectionDate ?? inspection.created_at).format(
              'DD/MM/YYYY',
            )}
          </b>
        </div>
        {inspection.status === WorkFlowStatus.CLOSE && (
          <div className="d-flex flex-nowrap justify-content-between">
            <FormLabel name={t('label_inspection_close_date')}>{`${t(
              'label_inspection_close_date',
            )}: `}</FormLabel>
            <b className="px-3">
              {moment(inspection.updated_at).format('DD/MM/YYYY')}
            </b>
          </div>
        )}
      </div>
    );
  };

  const getCancelledBy = () => {
    return (
      project?.team.find((t) => t.userId === inspection.cancelledBy)?.name ?? ''
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="start-inspection-modal"
      PaperProps={{
        style: {
          padding: 0,
          minWidth: 300,
        },
      }}
    >
      <DialogActions className="dialog-actions d-flex justify-content-between w-100">
        <div className="d-flex flex-wrap align-items-center">
          <DialogTitle className="p-1" color="inherit">
            {getInspectionTitle()}
          </DialogTitle>
          <div className="flex-grow-1 align-items-start">
            <span className={`status ${inspection.status?.toLowerCase()}`}>
              {inspection.status}
            </span>
          </div>
        </div>
        <IconButton className="close-btn" onClick={onClose}>
          <Close color="inherit" />
        </IconButton>
      </DialogActions>
      <FormControl className="p-4 d-flex flex-column overflow-auto">
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('inspection_id')}>{`${t(
            'inspection_id',
          )}:`}</FormLabel>
          <b className="px-3">{inspection?.displayId}</b>
        </div>
        {renderDate()}

        {inspection?.cancelledBy && (
          <div className="d-flex flex-nowrap justify-content-between">
            <FormLabel name={t('label_cancelled_by')}>{`${t(
              'label_cancelled_by',
            )}:`}</FormLabel>
            <b className="px-3">{getCancelledBy()}</b>
          </div>
        )}

        {inspection?.cancelReason && (
          <FormInput
            name={t('label_cancel_reason')}
            label={t('label_cancel_reason')}
            required={false}
            as="textarea"
            value={inspection?.cancelReason}
            disabled
          />
        )}

        {inspection?.rescheduleReason && (
          <FormInput
            name={t('label_reschedule_reason')}
            label={t('label_reschedule_reason')}
            required={false}
            as="textarea"
            value={inspection.rescheduleReason}
            disabled
          />
        )}

        {inspection.locations?.length > 0 && (
          <FormInput
            name={t('label_locations')}
            label={t('label_locations')}
            required={false}
            value={inspection.locations?.join(', ')}
            disabled
          />
        )}
        {subProjects.length > 0 ? (
          <FormInput
            name={t('label_sub_projects')}
            label={t('label_sub_projects')}
            required={false}
            value={subProjects}
            disabled
          />
        ) : null}

        <div className="d-flex flex-nowrap">
          <FormInput
            name={t('label_doer')}
            label={t('label_doer')}
            required={false}
            value={
              project?.team?.find(
                (teamObj) => teamObj.userId === inspection.doer,
              )?.name ?? ''
            }
            disabled
          />
          <FormInput
            name={t('label_inspector')}
            label={t('label_inspector')}
            className="ml-1"
            required={false}
            value={
              project?.team?.find(
                (teamObj) => teamObj.userId === inspection.inspector,
              )?.name ?? ''
            }
            disabled
          />
        </div>

        <footer className="p-0 mt-4 d-flex flex-row justify-content-between flex-grow-1 align-items-end">
          <Button variant="outlined" onClick={onClose}>
            {t('back')}
          </Button>
          <Button
            hidden={
              (project?.role === userRoles.DOER &&
                (inspection.status === WorkFlowStatus.SCHEDULED ||
                  inspection.status === WorkFlowStatus.DRAFT)) ||
              inspection.status === WorkFlowStatus.CANCELED
            }
            variant="contained"
            onClick={handleSubmit}
          >
            {getStartButtonLabel()}
          </Button>
        </footer>
      </FormControl>
    </Dialog>
  );
};
